import React, { useEffect, useState } from 'react';

import {
  Typography,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { getPayInConciliationSuggestions } from '../../../../../../../../../../lib/api/conciliations';
import {
  formatDate,
  formatMoney,
} from '../../../../../../../../../../lib/functions/utils';
import { Info, SettingsBackupRestore } from '@mui/icons-material';
import { CustomTooltip } from '../../../../../../../../../../lib/designSystem/TooltipTable';
import { LightTooltip } from '../../../../../../../../../../lib/designSystem/WhiteTooltip';
import GlobalSearcher, {
  Filter,
} from '../../../../../../../../../../lib/common/GlobalSearcher';

const ConciliationHistoryTooltip = ({
  conciliationLog,
}: {
  conciliationLog: any[];
}) => {
  // Función para formatear fecha
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  return (
    <div style={{ padding: 10, maxHeight: '300px', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
        <Typography variant="body1" fontWeight="bold">
          Historial de Conciliaciones
        </Typography>
      </div>
      <Table size="small" style={{ border: '1px solid #e0e0e0' }}>
        <TableBody>
          <TableRow style={{ backgroundColor: '#f5f5f5' }}>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Fecha
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Tipo
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Monto Total
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Documento
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Mora
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Saldo Antes
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Saldo Después
            </TableCell>
          </TableRow>
          {conciliationLog.map((log: any, index: number) => (
            <TableRow
              key={index}
              style={{
                backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9',
              }}
            >
              <TableCell style={{ textAlign: 'center' }}>
                {formatDate(log.date)}
              </TableCell>
              <TableCell style={{ textAlign: 'center' }}>
                {log.typeEsp}
              </TableCell>
              <TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
                {formatMoney(log.conciliationAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(log.conciliationDocumentAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(log.conciliationArrearAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(
                  log.documentAmountDebtBefore + log.arrearAmountDebtBefore,
                )}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(
                  log.documentAmountDebtAfter + log.arrearAmountDebtAfter,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const PaymentConciliationAssginationSuggested = (props: any) => {
  const { payIn, setDocumentsSuggested, documentsSuggested } = props;
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [typeSearch, setTypeSearch] = useState<string>('all');
  const [stakeholderSearch, setStakeholderSearch] = useState<string>('all');

  const setUp = async () => {
    setLoading(true);
    try {
      const response = await getPayInConciliationSuggestions(dispatch, {
        id: payIn.id,
      });
      setDocumentsSuggested([...response.documentsProcessed]);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    setUp();
  }, []);

  const filters: Filter[] = [
    {
      label: 'Tipo',
      type: 'select',
      options: [
        { label: 'Todos', value: 'all' },
        ...[...new Set(documentsSuggested.map((doc: any) => doc.type))].map(
          (type) => ({
            label: type as string,
            value: type as string,
          }),
        ),
      ],
      value: typeSearch,
      setter: setTypeSearch,
    },
    {
      label: 'Deudor',
      type: 'select',
      options: [
        { label: 'Todos', value: 'all' },
        ...[
          ...new Set(documentsSuggested.map((doc: any) => doc.stakeholderName)),
        ].map((stakeholder) => ({
          label: stakeholder as string,
          value: stakeholder as string,
        })),
      ],
      value: stakeholderSearch,
      setter: setStakeholderSearch,
    },
  ];

  const handleSelectAmount = (value: any, document: any) => {
    const amount = value.split('.').join('').split('$').join('');
    if (isNaN(amount)) return;

    const maxAmount = document.totalRemaining;

    console.log(maxAmount, Number(amount));
    if (maxAmount < Number(amount)) {
      alert('El monto no puede ser mayor a ninguna de las dos deudas');
    }
    const newInvoice = { ...document, amountToConciliate: Number(amount) };
    const newInvoices = documentsSuggested.map((doc: any) =>
      doc.id === document.id ? newInvoice : doc,
    );
    setDocumentsSuggested(newInvoices);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" padding="10px 0px" fontWeight={600}>
            Conciliaciones sugeridas
          </Typography>
          <LightTooltip
            title={
              <Typography variant="body1" style={{ padding: 4 }}>
                Los cálculos están basados en la fecha registrada del pago. Esta
                se encuentra arriba a la derecha
              </Typography>
            }
          >
            <Info
              fontSize="small"
              style={{ marginLeft: 5, cursor: 'pointer' }}
            />
          </LightTooltip>
        </div>
        <GlobalSearcher filters={filters} triggerSearch={async () => {}} />
      </div>
      <div
        style={{
          padding: '10px 0px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography fontWeight={600}>
          Total a conciliar:{' '}
          {formatMoney(
            documentsSuggested.reduce(
              (acc: any, inv: any) => acc + (inv.amountToConciliate || 0),
              0,
            ),
          )}
        </Typography>
      </div>

      {loading && <CircularProgress />}
      {!loading && (
        <div>
          {documentsSuggested.length ? (
            <Grid container spacing={1}>
              <Grid item xs={1}>
                <Typography variant="body1" fontWeight={600}>
                  Folio
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight={600} textAlign="center">
                  Monto
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight={600} textAlign="center">
                  Monto neto
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight={600} textAlign="center">
                  Conciliaciones
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body1" fontWeight={600} textAlign="center">
                  Saldo
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" fontWeight={600} textAlign="right">
                  Monto a conciliar
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body1">No hay facturas sugeridas</Typography>
          )}
          {documentsSuggested.map((document: any) => {
            if (
              typeSearch !== 'all' &&
              document.type !== typeSearch &&
              !document.amountToConciliate
            )
              return null;
            if (
              stakeholderSearch !== 'all' &&
              document.stakeholderName !== stakeholderSearch &&
              !document.amountToConciliate
            )
              return null;
            const tooltipTitleValues = [
              {
                name: 'Monto',
                value: formatMoney(document.amount),
              },
              {
                name: 'Fuente',
                value: document.type,
              },
              {
                name: 'Cliente',
                value: document.businessName,
              },
              {
                name: 'Rut cliente',
                value: document.businessIdentifier,
              },
              {
                name: 'Deudor',
                value: document.stakeholderName,
              },
              {
                name: 'Rut deudor',
                value: document.stakeholderIdentifier,
              },
              {
                name: 'Estado',
                value: document.status,
              },
              {
                name: 'Fecha de pago',
                value: formatDate(document.paymentDate),
              },
              {
                name: 'Días vencida',
                value: document.dueDays,
              },
              {
                name: 'Anticipo',
                value: `${document.advance}%`,
              },
            ];
            const remainingClient = document.totalRemainingNet;
            const remainingPayer = Math.max(
              document.totalRemainingNet,
              document.amount - document.amountConciliated,
            );
            const remainingAmount =
              payIn.businessOriginIdentifier === document.businessIdentifier
                ? remainingClient
                : remainingPayer;
            return (
              <Grid
                key={document.folio}
                container
                spacing={1}
                alignItems="center"
                style={{ marginTop: 1 }}
              >
                <Grid item xs={1}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LightTooltip
                      title={CustomTooltip(
                        tooltipTitleValues,
                        'Concepto',
                        'Valor',
                      )}
                    >
                      <Info
                        style={{
                          fontSize: 16,
                          cursor: 'pointer',
                        }}
                      />
                    </LightTooltip>
                    <Typography variant="body1" marginLeft={1}>
                      {document.folio}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography variant="body1" fontWeight={600}>
                      {formatMoney(document.amount)}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={2}>
                  <div style={{ textAlign: 'center' }}>
                    <Typography variant="body1" fontWeight={600}>
                      {formatMoney(document.amountNet)}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={2}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      {document.conciliationLog &&
                      document.conciliationLog.length > 0 ? (
                        <LightTooltip
                          title={
                            <ConciliationHistoryTooltip
                              conciliationLog={document.conciliationLog}
                            />
                          }
                          placement="right"
                        >
                          <Info
                            style={{
                              cursor: 'pointer',
                              fontSize: 14,
                            }}
                          />
                        </LightTooltip>
                      ) : null}
                      <Typography variant="body1" fontWeight={600}>
                        {formatMoney(document.amountConciliated)}
                      </Typography>
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      Doc: {formatMoney(document.documentConciliation)} | Mora:{' '}
                      {formatMoney(document.arrearConciliation)}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={3}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                      }}
                    >
                      <Typography variant="body1" fontWeight={600}>
                        {formatMoney(document.totalRemainingNet)}
                      </Typography>
                    </div>
                    <Typography variant="body2" color="textSecondary">
                      Doc: {formatMoney(document.documentRemainingAmountNet)} |
                      Mora: {formatMoney(document.arrearAmount)}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={2} textAlign="right">
                  <TextField
                    value={formatMoney(document.amountToConciliate)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            color="primary"
                            style={{ cursor: 'pointer' }}
                            disabled={
                              formatMoney(remainingAmount) ===
                              formatMoney(document.amountToConciliate)
                            }
                            onClick={() =>
                              handleSelectAmount(
                                formatMoney(remainingAmount),
                                document,
                              )
                            }
                          >
                            <SettingsBackupRestore />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: { textAlign: 'right' },
                    }}
                    onChange={(e) =>
                      handleSelectAmount(e.target.value, document)
                    }
                  />
                </Grid>
              </Grid>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PaymentConciliationAssginationSuggested;
